/**
 * BESTIX v5.0 — AccountPage
 * User dashboard with orders, listings, and settings
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Ticket, Settings, LogOut, Package, AlertCircle, Calendar, MapPin } from 'lucide-react';
import { useAuthStore, selectUser, selectIsAuthenticated } from '@/stores/auth.store';
import { api, type Order, type UserProfile } from '@/lib/api';

// ============================================
// Types
// ============================================

type TabId = 'orders' | 'listings' | 'settings';

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

// ============================================
// Helper Functions
// ============================================

function formatDate(dateString: string): string {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'fulfilled':
    case 'delivered':
      return 'bg-green-100 text-green-700';
    case 'confirmed':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'cancelled':
    case 'refunded':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// ============================================
// Components
// ============================================

function OrderCard({ order }: { order: Order }) {
  const totalTickets = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const firstItem = order.items[0];

  return (
    <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-400 font-mono">{order.orderNumber}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getStatusColor(order.status)}`}>
              {order.status}
            </span>
          </div>
          {firstItem && (
            <>
              <h3 className="font-semibold text-[var(--color-navy)]">{firstItem.eventName}</h3>
              <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                <MapPin className="w-3.5 h-3.5" />
                <span>{firstItem.venueName}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(firstItem.eventDate)}
                </span>
                <span className="flex items-center gap-1">
                  <Ticket className="w-3.5 h-3.5" />
                  {totalTickets} {totalTickets === 1 ? 'ticket' : 'tickets'}
                </span>
              </div>
            </>
          )}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-[var(--color-navy)]">
            {formatCurrency(order.totalCents, order.currency)}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Ordered {formatDate(order.createdAt)}
          </p>
          <Link 
            to={`/orders/${order.id}`}
            className="text-sm text-[var(--color-gold)] font-medium hover:underline mt-2 inline-block"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-navy)]" />
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, action }: {
  icon: React.ElementType;
  title: string;
  description: string;
  action?: { label: string; href: string };
}) {
  return (
    <div className="text-center py-12">
      <Icon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
      <h3 className="text-lg font-semibold text-[var(--color-navy)] mb-2">{title}</h3>
      <p className="text-gray-500 mb-4">{description}</p>
      {action && (
        <Link to={action.href} className="btn btn-primary">
          {action.label}
        </Link>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function AccountPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Auth state
  const user = useAuthStore(selectUser);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  
  // Local state
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tab = searchParams.get('tab');
    return (tab === 'orders' || tab === 'listings' || tab === 'settings') ? tab : 'orders';
  });
  
  // Data state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  
  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    phone: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Update tab when URL param changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'orders' || tab === 'listings' || tab === 'settings') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Initialize settings form from auth store user data
  useEffect(() => {
    if (user) {
      setSettingsForm({
        name: user.name || '',
        phone: '',
      });
    }
    setIsLoadingProfile(false);
  }, [user]);

  // Try to fetch extended profile data (optional - may not be available)
  useEffect(() => {
    async function fetchProfile() {
      if (!isAuthenticated) return;
      
      try {
        const data = await api.users.getProfile();
        if (data) {
          setProfile(data);
          setSettingsForm({
            name: data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}`.trim()
              : (data.displayName || ''),
            phone: data.phone || '',
          });
        }
      } catch {
        // Profile endpoint may not be available - that's okay, we have auth store data
      }
    }
    
    fetchProfile();
  }, [isAuthenticated]);

  // Fetch orders
  useEffect(() => {
    async function fetchOrders() {
      if (!isAuthenticated) return;
      
      setIsLoadingOrders(true);
      setOrdersError(null);
      
      try {
        const data = await api.orders.list({ limit: 20, sortOrder: 'desc' });
        // Handle both { orders: [...] } and direct array responses
        const ordersList = Array.isArray(data) ? data : (data.orders || []);
        setOrders(ordersList);
      } catch {
        // Orders endpoint may return empty or not be available yet
        setOrders([]);
      } finally {
        setIsLoadingOrders(false);
      }
    }
    
    fetchOrders();
  }, [isAuthenticated]);

  // Handle tab change
  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle settings save
  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      // Note: Profile update endpoint may not be available
      // For now, just show success (data is stored in auth)
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // Silently fail - profile endpoint may not exist
    } finally {
      setIsSaving(false);
    }
  };

  // Get user initials
  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0].toUpperCase() || 'U';

  const displayName = user?.name || 'User';

  const displayEmail = profile?.email || user?.email || '';

  const tabs: TabConfig[] = [
    { id: 'orders', label: 'My Orders', icon: <Ticket className="w-4 h-4" /> },
    { id: 'listings', label: 'My Listings', icon: <Package className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
  ];

  return (
    <>
      <Helmet><title>My Account | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-[var(--color-navy)] py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[var(--color-gold)] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {userInitials}
                </div>
                <div>
                  <h1 className="font-playfair text-2xl font-bold !text-white">{displayName}</h1>
                  <p className="text-gray-300">{displayEmail}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center gap-2 px-4 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[var(--color-navy)] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.id === 'orders' && orders && orders.length > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-[var(--color-gold)] text-white' : 'bg-gray-200'
                  }`}>
                    {orders.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-[var(--color-navy)] mb-4">Your Orders</h2>
              
              {ordersError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-700">{ordersError}</p>
                </div>
              )}
              
              {isLoadingOrders ? (
                <LoadingSpinner />
              ) : orders && orders.length > 0 ? (
                <>
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} />
                  ))}
                  <Link 
                    to="/events" 
                    className="block text-center text-[var(--color-navy)] font-medium hover:text-[var(--color-gold)] mt-6"
                  >
                    Browse More Events →
                  </Link>
                </>
              ) : (
                <EmptyState
                  icon={Ticket}
                  title="No orders yet"
                  description="Your ticket purchases will appear here."
                  action={{ label: 'Browse Events', href: '/events' }}
                />
              )}
            </div>
          )}

          {/* Listings Tab */}
          {activeTab === 'listings' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-[var(--color-navy)]">Your Listings</h2>
                <Link to="/sell" className="btn btn-primary text-sm">
                  + New Listing
                </Link>
              </div>
              <EmptyState
                icon={Package}
                title="No active listings"
                description="Start selling your tickets on BESTIX."
                action={{ label: 'Sell Tickets', href: '/sell' }}
              />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              <h2 className="text-xl font-semibold text-[var(--color-navy)] mb-6">Account Settings</h2>
              
              {profileError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-700">{profileError}</p>
                </div>
              )}

              {saveSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-700">Settings saved successfully!</p>
                </div>
              )}
              
              {isLoadingProfile ? (
                <LoadingSpinner />
              ) : (
                <div className="bg-white rounded-xl shadow-md divide-y">
                  <div className="p-6">
                    <h3 className="font-medium text-[var(--color-navy)] mb-4">Profile Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Name</label>
                        <input
                          type="text"
                          value={settingsForm.name}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-gold)] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          value={displayEmail}
                          disabled
                          className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">Contact support to change your email</p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={settingsForm.phone}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+1 (555) 000-0000"
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-gold)] focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-medium text-[var(--color-navy)] mb-4">Security</h3>
                    <button className="text-[var(--color-navy)] font-medium hover:text-[var(--color-gold)]">
                      Change Password →
                    </button>
                  </div>

                  <div className="p-6">
                    <h3 className="font-medium text-red-600 mb-4">Danger Zone</h3>
                    <button 
                      onClick={handleLogout}
                      className="flex items-center gap-2 text-red-600 font-medium hover:text-red-700"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out of All Devices
                    </button>
                  </div>
                </div>
              )}

              <button 
                onClick={handleSaveSettings}
                disabled={isSaving || isLoadingProfile}
                className="btn btn-primary mt-6 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
